/**
 * DragDropUpload + useClipboardPaste
 *
 * Componentes/hooks compartidos para CUALQUIER subida de archivo del
 * proyecto (imágenes, videos, PDFs, ZIPs, etc.). La regla del proyecto es:
 *
 *   "Toda zona de subida de imágenes/videos/documentos en la app DEBE
 *    soportar drag & drop y Ctrl+V (pegado de archivo desde el portapapeles)."
 *
 * Ver `docs/storage-y-visor.md` §6 ("Convención de UX para uploads") y
 * `CLAUDE.md` §7. Si abrís un nuevo modal con upload, usá `<DragDropUpload>`
 * y `useClipboardPaste()` directo — no copies/pegues `<Upload>` plano de
 * AntD ni inputs nativos.
 *
 * Por qué dos piezas separadas:
 *   - `<DragDropUpload>` es la **zona visible** que acepta drop y click.
 *     Wrapea `Upload.Dragger` de AntD con copy y estilos consistentes.
 *   - `useClipboardPaste()` es un **listener global** del documento que se
 *     activa con un flag (`active`) — típicamente `modalOpen`. Lo separamos
 *     porque a veces hay varios botones de upload en un modal y quieres que
 *     Ctrl+V vaya al "principal", no a todos a la vez.
 */

import React, { useEffect } from "react";
import { Upload, Typography, Spin } from "antd";
import { InboxOutlined, LoadingOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd";
import { mensajeVideoDemasiadoLargo } from "../utils/videoDuration";
import { getMsg } from "../utils/antdStatic";

/**
 * Pasa el archivo a `onFile` salvo que sea un video de más de 20 min: en ese
 * caso avisa y lo descarta antes de gastar datos subiéndolo (el backend
 * también lo rechaza). Cubre drag, click y Ctrl+V de todas las zonas de subida.
 */
async function entregarSiDuracionValida(file: File, onFile: (file: File) => void) {
  const aviso = await mensajeVideoDemasiadoLargo(file);
  if (aviso) {
    getMsg().error(aviso);
    return;
  }
  onFile(file);
}

const { Text } = Typography;
const { Dragger } = Upload;

// ─── useClipboardPaste ───────────────────────────────────────────────────────
//
// Hook que escucha el evento global `paste` del documento. Cuando el usuario
// pega un archivo desde el portapapeles (típicamente una captura de pantalla
// o un archivo copiado del explorador), invoca `onFile` para cada archivo
// que matchee `accept`.
//
// `active` controla si el listener está montado — pasale `modalOpen` para
// que solo dispare cuando el modal correspondiente está abierto. Sin este
// gate, dos modales con paste handler activos competirían por el mismo
// evento.
//
// `accept` es el mismo string que usa `<input type="file" accept="...">`
// (ej. `"image/*,video/*"`). Lo usamos para filtrar — solo invoca `onFile`
// para archivos cuyo MIME matchea. Si no quieres filtrar, pasa `undefined`.

interface UseClipboardPasteOptions {
  /** Si false, el listener no se monta (no hay paste). */
  active: boolean;
  /** Callback por cada archivo pegado que matchee `accept`. */
  onFile: (file: File) => void;
  /** Filtro MIME (ej. "image/*,video/*"). Default: cualquier archivo. */
  accept?: string;
  /** Si true, llama a `e.preventDefault()` cuando matchea. Default true. */
  preventDefault?: boolean;
}

/**
 * Convierte un string `accept` (ej. "image/*,video/*,.pdf,.zip") en un
 * predicado que recibe un File y devuelve true si matchea. Soporta:
 *   - wildcard MIME (`image/*`)
 *   - MIME exacto (`application/pdf`)
 *   - extensión por nombre (`.csv`, `.zip`) — los .csv pegados con Ctrl+V
 *     no traen MIME confiable en algunos browsers.
 *   - especial `*` o vacío → cualquier archivo matchea.
 */
function buildAcceptMatcher(accept?: string): (file: File) => boolean {
  if (!accept || accept.trim() === "*") return () => true;
  const tokens = accept
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return () => true;
  return (file) => {
    const m = (file.type || "").toLowerCase();
    const name = (file.name || "").toLowerCase();
    return tokens.some((tok) => {
      if (tok.startsWith(".")) {
        return name.endsWith(tok);
      }
      if (tok.endsWith("/*")) {
        const prefix = tok.slice(0, -1); // "image/"
        return m.startsWith(prefix);
      }
      return m === tok;
    });
  };
}

export function useClipboardPaste({
  active,
  onFile,
  accept,
  preventDefault = true,
}: UseClipboardPasteOptions) {
  useEffect(() => {
    if (!active) return undefined;
    const matches = buildAcceptMatcher(accept);
    const handler = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.files ?? []);
      const filtered = files.filter((f) => matches(f));
      if (filtered.length === 0) return;
      if (preventDefault) e.preventDefault();
      filtered.forEach((f) => void entregarSiDuracionValida(f, onFile));
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
  }, [active, onFile, accept, preventDefault]);
}

// ─── DragDropUpload ──────────────────────────────────────────────────────────
//
// Wrapper liviano sobre `Upload.Dragger` de AntD con copy y estilos
// consistentes. La idea es que TODAS las zonas de upload del sitio se vean
// y se comporten igual.
//
// API mínima — pasa todas las props extra a `<Dragger>`:
//   - `onFile(file)`: callback por cada archivo dropeado/clickeado/seleccionado.
//     Internamente devolvemos `false` desde `beforeUpload` para evitar que
//     AntD intente subir solo (todos los uploads del proyecto pasan por
//     `apiFetch` directo o cola pendiente — no usamos el HTTP del Upload).
//   - `accept`: filtro MIME nativo del input. Default `"image/*,video/*"`.
//   - `multiple`: aceptar varios archivos a la vez. Default true.
//   - `disabled`, `loading`: controles UI.
//   - `compact`: si true, reduce el padding (útil en modales chicos).
//   - `hint`: texto secundario debajo del título. Default genérico.

export interface DragDropUploadProps
  extends Omit<UploadProps, "beforeUpload" | "onChange" | "fileList"> {
  /** Callback por cada archivo. Se invoca SIEMPRE — drag, click y paste. */
  onFile: (file: File) => void;
  /** Filtro MIME nativo. Default `"image/*,video/*"`. */
  accept?: string;
  /** Default true (múltiples archivos a la vez). */
  multiple?: boolean;
  /** Loading state para mostrar feedback durante upload. */
  loading?: boolean;
  /** Reduce padding para modales chicos. */
  compact?: boolean;
  /** Texto principal (default "Arrastra archivos aquí o haz click"). */
  title?: string;
  /** Subtítulo (default menciona Ctrl+V). */
  hint?: string;
}

export const DragDropUpload: React.FC<DragDropUploadProps> = ({
  onFile,
  accept = "image/*,video/*",
  multiple = true,
  loading,
  disabled,
  compact = false,
  title = "Arrastra archivos aquí o haz click para seleccionar",
  hint = "También puedes pegar con Ctrl+V (captura de pantalla o archivo copiado)",
  ...rest
}) => {
  return (
    <Dragger
      multiple={multiple}
      accept={accept}
      showUploadList={false}
      disabled={disabled || loading}
      beforeUpload={(file) => {
        void entregarSiDuracionValida(file, onFile);
        // Devolver false evita que AntD haga su propia llamada HTTP. En el
        // proyecto todos los uploads pasan por apiFetch + storageService.
        return false;
      }}
      style={{
        padding: compact ? "8px 12px" : undefined,
        background: "#fafafa",
      }}
      {...rest}
    >
      {loading ? (
        // Feedback visible mientras el archivo se sube al backend. Sin esto la
        // zona quedaba estática y el usuario no sabía si estaba subiendo.
        <div style={{ padding: compact ? "6px 0" : "14px 0" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: compact ? 24 : 32 }} spin />} />
          <p
            style={{
              marginTop: 8,
              marginBottom: 0,
              fontSize: compact ? 13 : 14,
              fontWeight: 500,
              color: "#1677ff",
            }}
          >
            Subiendo…
          </p>
        </div>
      ) : (
        <>
          <p className="ant-upload-drag-icon" style={{ marginBottom: compact ? 4 : 8 }}>
            <InboxOutlined style={{ fontSize: compact ? 28 : 40, color: "#1677ff" }} />
          </p>
          <p
            className="ant-upload-text"
            style={{
              fontSize: compact ? 13 : 14,
              fontWeight: 500,
              marginBottom: 4,
            }}
          >
            {title}
          </p>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {hint}
          </Text>
        </>
      )}
    </Dragger>
  );
};

export default DragDropUpload;
